import { useState, useEffect } from 'react'
import { ChevronDownIcon, ChevronRightIcon, ShieldCheckIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import type { ComplianceViolation, PolicyCriticality, DiagnosticsData, PolicyCheckDiagnostics } from '@/types'
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid'
import { refMatchesFramework, FRAMEWORK_PATTERNS } from '@/lib/frameworkUtils'
import ScoreDeltaChip from '@/components/Dashboard/v2/ScoreDeltaChip'

// Helper to find the framework reference matching the current context
function getContextualFrameworkRef(refs: string[], frameworkCode?: string): string | null {
  if (!frameworkCode || refs.length === 0) return refs[0] ?? null

  const matchingRef = refs.find(ref => refMatchesFramework(ref, frameworkCode))

  // Only return matching ref for the current framework - no fallback to unrelated refs
  return matchingRef ?? null
}

// Filter framework refs to only those matching the current framework context
function getFrameworkSpecificRefs(refs: string[], frameworkCode?: string): string[] {
  if (!frameworkCode) return refs
  return refs.filter(ref => refMatchesFramework(ref, frameworkCode))
}

// Count distinct frameworks a set of refs maps to (cross-framework view only).
function countDistinctFrameworks(refs: string[]): number {
  const frameworkCodes = Object.keys(FRAMEWORK_PATTERNS)
  const matched = new Set<string>()
  for (const ref of refs) {
    for (const code of frameworkCodes) {
      if (refMatchesFramework(ref, code)) {
        matched.add(code)
        break
      }
    }
  }
  return matched.size
}

// Derive the policy type label for policyAnalysis diagnostics
function getPolicyTypeLabel(violation: ComplianceViolation): string | null {
  const product = (violation.product || '').toLowerCase()
  if (product === 'entraid' || product === 'azuread') return 'Conditional Access'
  if (product === 'sharepoint') return 'Conditional Access'
  return null
}

// Format diagnostic values for display
function formatValue(val: unknown): string {
  if (val === true) return 'True'
  if (val === false) return 'False'
  if (val === null || val === undefined) return 'Not set'
  return String(val)
}

// Editorial short-form date for the expired-waiver badge (e.g., "May 5").
// Falls back to locale date string when month/day aren't resolvable.
function formatShortDate(iso: string | undefined | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

interface ViolationRowProps {
  violation: ComplianceViolation
  onAddWaiver?: (violation: ComplianceViolation) => void
  onEditWaiver?: (violation: ComplianceViolation) => void
  onDeleteWaiver?: (violation: ComplianceViolation) => void
  frameworkCode?: string  // e.g., "EIDSCA", "SCUBA", "NIST_800_53"
  id?: string
  defaultExpanded?: boolean
}

/**
 * Single violation row with criticality-based border colors and expand/collapse for details.
 * Design follows Section 10.3 of the implementation plan.
 */
export default function ViolationRow({ violation, onAddWaiver, onEditWaiver, onDeleteWaiver, frameworkCode, id, defaultExpanded }: ViolationRowProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded ?? false)

  useEffect(() => {
    if (defaultExpanded) setIsExpanded(true)
  }, [defaultExpanded])

  // Get framework references (all are now primary/implemented)
  const frameworkRefs = violation.frameworkReferences || []

  // Get contextual framework reference - shows the reference matching the current framework context
  const contextualRef = getContextualFrameworkRef(frameworkRefs, frameworkCode)

  // Filter refs to only show those relevant to the current framework context
  const contextualRefs = getFrameworkSpecificRefs(frameworkRefs, frameworkCode)

  // Parse diagnostics JSON if available
  const diagnostics: DiagnosticsData | null = (() => {
    if (!violation.diagnostics) return null
    try {
      const parsed = JSON.parse(violation.diagnostics)
      // Backward compat: if no type field but has policyAnalysis, treat as CA-style
      if (!parsed.type && parsed.policyAnalysis) {
        return { ...parsed, type: 'policyAnalysis' } as PolicyCheckDiagnostics
      }
      return parsed as DiagnosticsData
    } catch {
      return null
    }
  })()

  // Get border color based on criticality
  const getBorderColor = (criticality: PolicyCriticality): string => {
    switch (criticality) {
      case 'SHALL':
        return 'border-red-500/30'
      case 'SHOULD':
        return 'border-amber-500/30'
      case 'MAY':
      default:
        return 'border-dark-600'
    }
  }

  // Criticality badge styling
  const CriticalityBadge = ({ criticality }: { criticality: PolicyCriticality }) => {
    const getStyles = () => {
      switch (criticality) {
        case 'SHALL':
          return 'bg-red-500/15 text-red-400 border-red-500/30'
        case 'SHOULD':
          return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
        case 'MAY':
        default:
          return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
      }
    }

    // Tooltip frames the criticality tier conversationally and bridges
    // once to the GS chip so users don't read the two signals as the
    // same axis. Score lift is driven by the policy's framework weight,
    // not its SHALL/SHOULD tier — so two policies of similar weight can
    // produce the same lift regardless of tier.
    const getTooltip = () => {
      switch (criticality) {
        case 'SHALL':
          return "Must-fix per the frameworks. The GS chip on this row is separate: it's the score lift you'd get from closing this."
        case 'SHOULD':
          return "Recommended by the frameworks. The GS chip on this row shows the score lift you'd get from closing this."
        case 'MAY':
        default:
          return "Optional or aspirational. The GS chip on this row shows the score lift you'd get from closing this."
      }
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStyles()}`}
        title={getTooltip()}
        aria-label={getTooltip()}
      >
        {criticality}
      </span>
    )
  }

  // Status badge styling
  const StatusBadge = ({ status }: { status: string }) => {
    const getStyles = () => {
      switch (status) {
        case 'Open':
          return 'bg-red-500/15 text-red-400 border-red-500/30'
        case 'Waived':
          return 'bg-amber-500/15 text-amber-400 border-amber-500/30'
        case 'Resolved':
        case 'Pass':
          return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30'
        case 'DataUnavailable':
          return 'bg-amber-500/10 text-amber-400/70 border-amber-500/20'
        default:
          return 'bg-slate-500/15 text-slate-400 border-slate-500/30'
      }
    }

    const displayLabel = status === 'DataUnavailable' ? 'No Data' : status

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getStyles()}`}
      >
        {displayLabel}
      </span>
    )
  }

  return (
    <div
      id={id}
      className={`bg-dark-800 rounded-xl border p-4 ${getBorderColor(violation.criticality)}`}
    >
      {/* Main row - always visible */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Policy code, framework ID, and criticality badge */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="text-xs font-mono text-blue-400" title="Policy ID">
              {violation.policyCode}
            </span>
            {frameworkRefs.length > 0 && contextualRef ? (
              <>
                <span className="text-slate-600">|</span>
                <span
                  className="text-xs font-mono text-slate-400"
                  title={`Framework Reference: ${contextualRef}`}
                >
                  {contextualRef}
                </span>
              </>
            ) : (
              <>
                <span className="text-slate-600">|</span>
                <span className="text-xs text-slate-500">
                  {violation.category} • {violation.product}
                </span>
              </>
            )}
            <CriticalityBadge criticality={violation.criticality} />
            {violation.hasWaiver && violation.waiver && (
              violation.waiver.isExpired ? (
                // Expired waiver: muted amber italic so the user sees the
                // verdict at a glance and knows the exception is no longer
                // protecting this finding. Editorial voice, short and direct.
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs italic font-medium bg-amber-500/10 text-amber-400/90 border border-amber-500/30"
                  title="This waiver has expired. The violation will return to Open after the next overnight sweep, or remove the waiver here to flip it now."
                >
                  Waiver expired {formatShortDate(violation.waiver.expiresAt)}
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-500/15 text-primary-400 border border-primary-500/30">
                  {violation.waiver.waiverType === 'Permanent'
                    ? 'Alternative Mitigation'
                    : violation.waiver.waiverType === 'Temporary'
                      ? 'Temporary Exception'
                      : 'N/A'}
                </span>
              )
            )}
          </div>

          {/* Title with Guardian Score lift chip — chip self-hides when delta is null. */}
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-medium text-slate-200">{violation.policyTitle}</h4>
            <ScoreDeltaChip delta={violation.guardianScoreDelta} />
          </div>

          {/* Message */}
          <p className="text-xs text-slate-400 mt-1 line-clamp-2">{violation.message}</p>

          {/* Framework control summary - count badge (full list lives in expanded view) */}
          {(() => {
            if (frameworkCode) {
              const extra = contextualRefs.length - 1
              if (extra <= 0) return null
              return (
                <div className="mt-2">
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-dark-700 text-slate-400 font-mono">
                    +{extra} more {extra === 1 ? 'control' : 'controls'}
                  </span>
                </div>
              )
            }
            if (frameworkRefs.length <= 1) return null
            const frameworkCount = countDistinctFrameworks(frameworkRefs)
            const controlsLabel = `${frameworkRefs.length} controls`
            const frameworksLabel = frameworkCount > 1 ? `${frameworkCount} frameworks` : null
            return (
              <div className="mt-2">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-dark-700 text-slate-400 font-mono">
                  {frameworksLabel ? `${frameworksLabel} · ${controlsLabel}` : controlsLabel}
                </span>
              </div>
            )
          })()}

          {/* Metadata row */}
          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
            <span>{violation.category}</span>
            <span className="text-slate-700">|</span>
            <span>{violation.product}</span>
          </div>
        </div>

        {/* Right side actions */}
        <div className="flex items-center gap-2 ml-4 flex-shrink-0">
          <StatusBadge status={violation.status} />
          {!violation.hasWaiver && violation.status === 'Open' && onAddWaiver && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onAddWaiver(violation)
              }}
              className="text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              Add Waiver
            </button>
          )}
          {violation.hasWaiver && onEditWaiver && (
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEditWaiver(violation)
              }}
              className="text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors"
            >
              Edit Waiver
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded hover:bg-dark-700 transition-colors"
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronRightIcon className="h-4 w-4 text-slate-400" />
            )}
          </button>
        </div>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-dark-700 space-y-4">
          {/* Details section */}
          {violation.details && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Details
              </span>
              <p className="mt-1 text-sm text-slate-300">{violation.details}</p>
            </div>
          )}

          {/* Policy Analysis - diagnostic breakdown from Rego */}
          {diagnostics?.type === 'policyAnalysis' && diagnostics.policyAnalysis.length > 0 && (() => {
            const policyTypeLabel = getPolicyTypeLabel(violation)
            return (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Policy Analysis
              </span>
              <p className="mt-1 text-xs text-slate-400">
                {diagnostics.totalEnabledPolicies} Enabled {policyTypeLabel ? `${policyTypeLabel} ` : ''}{diagnostics.totalEnabledPolicies === 1 ? 'policy' : 'policies'} analyzed
              </p>
              <div className="mt-2 space-y-2">
                {diagnostics.policyAnalysis.map((policy) => {
                  const isNearMiss = policy.passedCount >= policy.totalChecks - 1 && policy.passedCount < policy.totalChecks
                  return (
                    <div
                      key={policy.id}
                      className={`rounded-lg p-3 ${
                        isNearMiss
                          ? 'bg-amber-500/5 border border-amber-500/20'
                          : 'bg-dark-900/50 border border-dark-700'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-300 font-medium truncate">{policy.name}</span>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {isNearMiss && (
                            <span className="text-[10px] font-medium text-amber-400 bg-amber-500/10 px-1.5 py-0.5 rounded">
                              Near Miss
                            </span>
                          )}
                          <span className={`text-xs font-mono ${
                            policy.passedCount === policy.totalChecks
                              ? 'text-emerald-400'
                              : isNearMiss
                                ? 'text-amber-400'
                                : 'text-slate-500'
                          }`}>
                            {policy.passedCount}/{policy.totalChecks}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {diagnostics.requiredConditions.map((condition) => {
                          const passed = policy.checks[condition]
                          const label = diagnostics.conditionLabels[condition] ?? condition
                          return (
                            <span
                              key={condition}
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${
                                passed
                                  ? 'bg-emerald-500/10 text-emerald-400'
                                  : 'bg-red-500/10 text-red-400'
                              }`}
                            >
                              {passed ? (
                                <CheckCircleIcon className="h-3 w-3" />
                              ) : (
                                <XCircleIcon className="h-3 w-3" />
                              )}
                              {label}
                            </span>
                          )
                        })}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
            )
          })()}

          {/* Setting Checks - configuration analysis from Rego */}
          {diagnostics?.type === 'settingChecks' && diagnostics.settingChecks.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Configuration Analysis
              </span>
              <div className="mt-2 space-y-1.5">
                {diagnostics.settingChecks.map((check) => (
                  <div key={check.setting} className="flex items-center justify-between bg-dark-900/50 rounded-lg px-3 py-2 border border-dark-700">
                    <div className="flex items-center gap-2">
                      {check.passed ? (
                        <CheckCircleIcon className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      ) : (
                        <XCircleIcon className="h-4 w-4 text-red-400 flex-shrink-0" />
                      )}
                      <span className="text-sm text-slate-300">{check.label}</span>
                      {check.context && <span className="text-xs text-slate-500">({check.context})</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs font-mono">
                      <span className="text-slate-500">Expected: <span className="text-slate-300">{formatValue(check.expected)}</span></span>
                      <span className="text-slate-500">Actual: <span className={check.passed ? 'text-emerald-400' : 'text-red-400'}>{formatValue(check.actual)}</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Item List - user/resource list diagnostics from Rego */}
          {diagnostics?.type === 'itemList' && diagnostics.items.length > 0 && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">{diagnostics.label}</span>
              <p className="mt-1 text-xs text-slate-400">{diagnostics.totalCount} items found</p>
              <div className="mt-2 space-y-1">
                {diagnostics.items.slice(0, 10).map((item, i) => (
                  <div key={i} className="flex items-center gap-2 bg-dark-900/50 rounded px-3 py-1.5 border border-dark-700">
                    <span className="text-slate-300 font-mono text-xs">{item.name}</span>
                    {item.detail && <span className="text-slate-500 text-xs">({item.detail})</span>}
                  </div>
                ))}
                {diagnostics.totalCount > 10 && (
                  <p className="text-xs text-slate-500 pl-3">...and {diagnostics.totalCount - 10} more</p>
                )}
              </div>
            </div>
          )}

          {/* Data Unavailable - license-gated or missing data diagnostics */}
          {diagnostics?.type === 'dataUnavailable' && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/15 text-amber-400 border border-amber-500/30">
                  Data Unavailable
                </span>
              </div>
              <p className="text-sm text-slate-300">{diagnostics.reason}</p>
              <p className="text-xs text-slate-500 mt-1 font-mono">Data path: {diagnostics.dataPath}</p>

              {diagnostics.licenseRequired ? (
                <div className="mt-3 rounded-lg bg-dark-900/50 border border-dark-700 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-amber-400 uppercase tracking-wide">Required License</span>
                  </div>
                  <p className="text-sm font-medium text-slate-200">{diagnostics.licenseRequired.name}</p>
                  <p className="text-xs text-slate-400">{diagnostics.licenseRequired.description}</p>
                  {diagnostics.licenseRequired.infoUrl && (
                    <a
                      href={diagnostics.licenseRequired.infoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 transition-colors"
                    >
                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                      Learn about this license
                    </a>
                  )}
                  <p className="text-xs text-slate-500 italic">
                    If this license is not needed for your organization, add a &lsquo;Not Applicable&rsquo; waiver to dismiss this finding.
                  </p>
                </div>
              ) : (
                <div className="mt-3 rounded-lg bg-dark-900/50 border border-dark-700 p-3">
                  <p className="text-xs text-slate-400">
                    The required data could not be collected. This may be due to a data collection issue or a service configuration that prevents access.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Affected resource */}
          {violation.affectedResource && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Affected Resource
              </span>
              <p className="mt-1 text-sm text-slate-300 font-mono">{violation.affectedResource}</p>
            </div>
          )}

          {/* Framework references */}
          {frameworkRefs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheckIcon className="h-3.5 w-3.5 text-primary-400" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Framework Controls
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {frameworkRefs.map((ref, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 rounded text-xs bg-primary-500/15 text-primary-300 border border-primary-500/30"
                  >
                    {ref}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Remediation steps and URL */}
          {(violation.remediationSteps || violation.remediationUrl) && (
            <div>
              <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                Remediation
              </span>
              {violation.remediationSteps && (
                <p className="mt-1 text-sm text-slate-300">{violation.remediationSteps}</p>
              )}
              {violation.remediationUrl && (
                <a
                  href={violation.remediationUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary-400 hover:text-primary-300 transition-colors"
                >
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                  </svg>
                  View full instructions on Microsoft Learn
                </a>
              )}
            </div>
          )}

          {/* Waiver info if present */}
          {violation.hasWaiver && violation.waiver && (
            <div className="bg-dark-900/50 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-primary-400 uppercase tracking-wide">
                  Waiver Details
                </span>
                {onDeleteWaiver && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteWaiver(violation)
                    }}
                    className="text-xs text-red-400 hover:text-red-300 font-medium transition-colors"
                  >
                    Remove Waiver
                  </button>
                )}
              </div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Type:</span>
                  <span className="text-slate-300">
                    {violation.waiver.waiverType === 'Permanent'
                      ? 'Alternative Mitigation'
                      : violation.waiver.waiverType === 'Temporary'
                        ? 'Temporary Exception'
                        : 'Not Applicable'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Reason:</span>
                  <span className="text-slate-300">{violation.waiver.reason}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Approved by:</span>
                  <span className="text-slate-300">{violation.waiver.approvedBy}</span>
                </div>
                {violation.waiver.expiresAt && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">
                      {violation.waiver.isExpired ? 'Expired:' : 'Expires:'}
                    </span>
                    <span className={violation.waiver.isExpired ? 'text-amber-400 italic' : 'text-slate-300'}>
                      {new Date(violation.waiver.expiresAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {violation.waiver.isExpired && (
                  <p className="mt-2 text-xs italic text-amber-400/80">
                    This waiver has expired. The violation flips back to Open after the overnight sweep, or remove the waiver here to flip it now.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Detection timestamp.
              "First detected" reflects when this current open episode began —
              held immutable across rescans by StandardsComplianceService, and
              reset only when the policy resolves and re-fails. The bare
              "Last checked" carries the most-recent scan touch so the user
              can see the row is fresh, without confusing it with first detection. */}
          <div className="text-xs text-slate-500">
            First detected {formatShortDate(violation.firstDetectedAt ?? violation.detectedAt)}
            <span className="ml-4">
              Last checked {formatShortDate(violation.detectedAt)}
            </span>
            {violation.resolvedAt && (
              <span className="ml-4">
                Resolved {formatShortDate(violation.resolvedAt)}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
